import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  UserCircle, Plus, Search, RefreshCcw, Edit, Mail, 
  CheckCircle, XCircle, Key, Building2, Clock
} from 'lucide-react';

const CfaAdminsPage = ({ onMenuClick }) => {
  const [admins, setAdmins] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCenter, setFilterCenter] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showOrgDialog, setShowOrgDialog] = useState(false);
  const [orgAdmin, setOrgAdmin] = useState(null);
  const [orgId, setOrgId] = useState('');
  const [savingOrg, setSavingOrg] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    organization_id: ''
  });

  const fetchAdmins = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCenter !== 'all') params.append('center_id', filterCenter);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await api.get(`/super-admin/cfa-admins?${params}`);
      setAdmins(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filterCenter, filterStatus, searchTerm]);

  const fetchCenters = useCallback(async () => {
    try {
      const response = await api.get('/super-admin/centers');
      setCenters(response.data);
    } catch (error) {
      console.error('Error fetching centers:', error);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
    fetchCenters();
  }, [fetchAdmins, fetchCenters]);

  const handleCreate = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.organization_id) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    try {
      await api.post('/super-admin/cfa-admins', formData);
      toast.success('Admin CFA créé - Email d\'activation envoyé');
      setShowCreateDialog(false);
      setFormData({ first_name: '', last_name: '', email: '', organization_id: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleToggleStatus = async (admin) => {
    try {
      await api.put(`/super-admin/cfa-admins/${admin.id}`, {
        is_active: !admin.is_active
      });
      toast.success(admin.is_active ? 'Admin désactivé' : 'Admin activé');
      fetchAdmins();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleResendActivation = async (admin) => {
    try {
      await api.post(`/super-admin/cfa-admins/${admin.id}/resend-activation`);
      toast.success('Email d\'activation renvoyé');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleResetAccess = async (admin) => {
    try {
      await api.post(`/super-admin/cfa-admins/${admin.id}/reset-access`);
      toast.success('Accès réinitialisé - Email envoyé');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const openOrgDialog = (admin) => {
    setOrgAdmin(admin);
    setOrgId(admin.organization_id ? admin.organization_id.toString() : '');
    setShowOrgDialog(true);
  };

  const handleSaveOrg = async () => {
    if (!orgAdmin) return;
    setSavingOrg(true);
    try {
      await api.put(`/super-admin/users/${orgAdmin.id}/organization`, {
        organization_id: orgId && orgId !== 'none' ? parseInt(orgId) : null,
      });
      toast.success('Organisation mise à jour');
      setShowOrgDialog(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingOrg(false);
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="cfa-admins-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onMenuClick} className="md:hidden p-2 -ml-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800" aria-label="Ouvrir le menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Gestion des Admin CFA
            </h1>
            <p className="text-slate-500">{admins.length} administrateur(s)</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-violet-600 hover:bg-violet-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel Admin CFA
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterCenter} onValueChange={setFilterCenter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Centre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les centres</SelectItem>
                {centers.map(c => (
                  <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      ) : (
        <div className="space-y-3">
          {admins.map(admin => (
            <Card key={admin.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                      <UserCircle className="w-6 h-6 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">
                        {admin.first_name} {admin.last_name}
                      </p>
                      <p className="text-sm text-slate-500">{admin.email}</p>
                      {admin.organization && (
                        <p className="text-sm text-violet-600 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {admin.organization.name}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      {admin.last_login_at ? (
                        <p className="text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Dernière connexion: {new Date(admin.last_login_at).toLocaleDateString()}
                        </p>
                      ) : (
                        <p className="text-slate-400">Jamais connecté</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant={admin.is_active ? 'default' : 'secondary'}>
                        {admin.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                      {admin.is_email_verified ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Vérifié
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-600">
                          En attente
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openOrgDialog(admin)}
                        title="Changer l'organisation"
                      >
                        <Building2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResendActivation(admin)}
                        title="Renvoyer email d'activation"
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetAccess(admin)}
                        title="Réinitialiser accès"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button
                        variant={admin.is_active ? "outline" : "default"}
                        size="sm"
                        onClick={() => handleToggleStatus(admin)}
                        className={admin.is_active ? "text-red-600" : "bg-emerald-600"}
                      >
                        {admin.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un Admin CFA</DialogTitle>
            <DialogDescription>Un email d'activation sera envoyé</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Centre de rattachement</Label>
              <Select 
                value={formData.organization_id} 
                onValueChange={(v) => setFormData({...formData, organization_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un centre" />
                </SelectTrigger>
                <SelectContent>
                  {centers.map(c => (
                    <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
            <Button onClick={handleCreate} className="bg-violet-600 hover:bg-violet-700">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showOrgDialog} onOpenChange={setShowOrgDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer l'organisation</DialogTitle>
            <DialogDescription>
              {orgAdmin && `${orgAdmin.first_name} ${orgAdmin.last_name} (${orgAdmin.email})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Organisation</Label>
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une organisation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune organisation</SelectItem>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {orgAdmin && !orgAdmin.organization_id && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                ⚠ Cet utilisateur n'a actuellement aucune organisation liée
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrgDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveOrg}
              disabled={savingOrg}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {savingOrg ? 'Mise à jour...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CfaAdminsPage;
